import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';
import { useToastStore } from '../../../stores/toast-store';

import type {
  EstablishLinkRequest,
  EstablishLinkResponse,
  LinkCandidatesResponse,
} from '../../../lib/types';

/**
 * F2-a — estabelece/evolui o vínculo (Bond↔Person) da conversa (fecha o 409 do
 * confirm-link). Ao sucesso invalida a conversa, a fila, o papel do contato e o
 * bond afetado; toast honesto no erro (o 409 vira uma mensagem, não silêncio).
 */
export function useEstablishLink(): {
  establish: (body: EstablishLinkRequest) => Promise<EstablishLinkResponse>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (body: EstablishLinkRequest) => api.establishLink(body),
    onSuccess: (result, body) => {
      void queryClient.invalidateQueries({ queryKey: ['conversation', body.conversationId] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['contact-role', body.conversationId] });
      void queryClient.invalidateQueries({ queryKey: ['bond', result.bondId] });
      void queryClient.invalidateQueries({ queryKey: ['persons'] });
      useToastStore.getState().push({
        variant: 'success',
        title: 'Vínculo estabelecido',
        description: result.identityVerified
          ? 'Titular avalizado e identidade confirmada.'
          : 'Vínculo registrado nesta conversa.',
        durationMs: 3500,
      });
    },
    onError: (err) => {
      useToastStore.getState().push({
        variant: 'error',
        title: 'Não foi possível estabelecer o vínculo',
        description: err instanceof Error ? err.message : 'Erro desconhecido',
        durationMs: 6000,
      });
    },
  });
  return {
    establish: (body) => mutation.mutateAsync(body),
    isPending: mutation.isPending,
  };
}

/**
 * F2-a — candidatos de vínculo para o fluxo de estabelecimento. `enabled` deve
 * carregar o gate (capability + fluxo aberto) para não buscar à toa.
 */
export function useLinkCandidates(
  conversationId: string | undefined,
  identifier: string | undefined,
  enabled: boolean,
): {
  data: LinkCandidatesResponse | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: ['link-candidates', conversationId, identifier ?? ''],
    queryFn: () => api.listLinkCandidates(conversationId!, identifier),
    enabled: enabled && conversationId !== undefined,
  });
  return { data: query.data, isLoading: query.isLoading, isError: query.isError };
}
